<template>
  <div class="directions-tab">
    <div class="mode-row">
      <div class="mode-chips">
        <button
          v-for="mode in MODES"
          :key="mode.id"
          class="mode-btn"
          type="button"
          :class="{ active: activeMode === mode.id }"
          @click="activeMode = mode.id"
        >
          {{ mode.label }}
        </button>
      </div>
      <div class="limit-chips">
        <button
          v-for="opt in LIMITS"
          :key="String(opt.id)"
          class="limit-btn"
          type="button"
          :class="{ active: rowLimit === opt.id }"
          @click="rowLimit = opt.id"
        >
          {{ opt.label }}
        </button>
      </div>
    </div>

    <div v-if="modeHint" class="mode-hint">{{ modeHint }}</div>

    <div class="chart-layout">
      <div class="chart-settings">
        <div class="settings-title">Настройки графика</div>
        <div class="settings-section" v-for="group in metricGroups" :key="group.label">
          <div class="settings-label">{{ group.label }}</div>
          <div class="metrics-list">
            <button
              v-for="metric in group.metrics"
              :key="metric.field"
              type="button"
              class="metric-chip"
              :class="{ selected: isSelected(metric) }"
              :title="isSelected(metric) ? 'Убрать метрику (Shift+клик — только эта)' : metric.label"
              @click="toggleMetric(metric, $event)"
            >
              <span class="metric-dot" :style="{ background: metric.color }" />
              {{ metric.label }}
            </button>
          </div>
        </div>
        <div class="settings-section">
          <div v-if="selectedFields.length > 1" class="settings-hint">Несколько метрик — сгруппированные бары</div>
          <div class="settings-hint">Shift+клик — оставить только эту метрику</div>
        </div>
      </div>
      <div class="chart-block">
        <LogistsCompareChart
          title="Направления: сравнение метрик"
          :rows="chartRows"
          :metrics="selectedMetrics"
          :empty-text="isLoading ? 'Загрузка…' : 'Нет данных по выбранным фильтрам'"
        />
      </div>
    </div>

    <div class="table-scroll">
      <table class="stat-table" v-if="tableRows.length">
        <thead>
          <tr>
            <th class="sortable" :class="{ active: sortKey === 'direction' }" @click="setSort('direction')">
              Направление <span v-if="sortKey === 'direction'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'trips' }" @click="setSort('trips')">
              Рейсов <span v-if="sortKey === 'trips'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num cell-wide sortable" :class="{ active: sortKey === 'avg_margin_pct' }" @click="setSort('avg_margin_pct')">
              Ср. маржа <span v-if="sortKey === 'avg_margin_pct'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num cell-wide sortable" :class="{ active: sortKey === 'avg_profitability_pct' }" @click="setSort('avg_profitability_pct')">
              Ср. рентаб. <span v-if="sortKey === 'avg_profitability_pct'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'total_commission' }" @click="setSort('total_commission')">
              Комиссия <span v-if="sortKey === 'total_commission'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'avg_commission' }" @click="setSort('avg_commission')">
              Ср. комиссия <span v-if="sortKey === 'avg_commission'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'avg_search_sec' }" @click="setSort('avg_search_sec')">
              Ср. поиск, ч. <span v-if="sortKey === 'avg_search_sec'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num" title="Распределение закрытий по запасу до погрузки: ≥24 ч · 12–24 ч · &lt;12 ч">
              Закрытие
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'avg_hours_to_load' }" @click="setSort('avg_hours_to_load')" title="Среднее время от закрытия до погрузки, ч">
              До погрузки, ч <span v-if="sortKey === 'avg_hours_to_load'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="num sortable" :class="{ active: sortKey === 'avg_created_lead_hours' }" @click="setSort('avg_created_lead_hours')" title="Средний запас от создания карточки до плановой погрузки, ч — насколько заранее заведены рейсы">
              Создан заранее, ч <span v-if="sortKey === 'avg_created_lead_hours'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="sortable" :class="{ active: sortKey === 'first_trip_date' }" @click="setSort('first_trip_date')">
              Первый рейс <span v-if="sortKey === 'first_trip_date'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
            <th class="sortable" :class="{ active: sortKey === 'last_trip_date' }" @click="setSort('last_trip_date')">
              Последний <span v-if="sortKey === 'last_trip_date'" class="sort-arrow">{{ sortDir === 'desc' ? '▼' : '▲' }}</span>
            </th>
          </tr>
        </thead>
        <tbody>
          <tr v-for="row in tableRows" :key="row.direction">
            <td class="dir-cell">
              {{ row.direction }}
              <span v-if="activeMode === 'new'" class="first-badge">новое</span>
              <span v-else-if="activeMode === 'stale'" class="stale-badge">неактивно</span>
            </td>
            <td class="num">{{ fmtNum(row.trips) }}</td>
            <td class="num cell-wide">
              <div class="bar-cell">
                <div class="bar-track">
                  <div class="bar-fill" :class="barClass(row.avg_margin_pct)" :style="barStyle(row.avg_margin_pct, maxMargin)"></div>
                </div>
                <span class="bar-value" :class="negClass(row.avg_margin_pct)">{{ fmtPct(row.avg_margin_pct) }}</span>
              </div>
            </td>
            <td class="num cell-wide">
              <div class="bar-cell">
                <div class="bar-track">
                  <div class="bar-fill" :class="barClass(row.avg_profitability_pct)" :style="barStyle(row.avg_profitability_pct, maxProfitability)"></div>
                </div>
                <span class="bar-value" :class="negClass(row.avg_profitability_pct)">{{ fmtPct(row.avg_profitability_pct) }}</span>
              </div>
            </td>
            <td class="num strong" :class="negClass(row.total_commission)">{{ fmtMoney(row.total_commission) }}</td>
            <td class="num">{{ fmtMoney(row.avg_commission) }}</td>
            <td class="num">{{ fmtHoursNum(row.avg_search_sec) }}</td>
            <td class="num">
              <div
                v-if="closeTotal(row) > 0"
                class="close-widget"
                :title="closeTitle(row)"
              >
                <div class="close-track">
                  <div class="close-seg seg-early" :style="{ width: closePct(row, 'closed_early') + '%' }"></div>
                  <div class="close-seg seg-lastday" :style="{ width: closePct(row, 'closed_lastday') + '%' }"></div>
                  <div class="close-seg seg-late" :style="{ width: closePct(row, 'closed_late') + '%' }"></div>
                </div>
              </div>
              <span v-else>—</span>
            </td>
            <td class="num">{{ fmtNum(row.avg_hours_to_load, 1) }}</td>
            <td class="num">{{ fmtNum(row.avg_created_lead_hours, 1) }}</td>
            <td>{{ fmtDate(row.first_trip_date) }}</td>
            <td>{{ fmtDate(row.last_trip_date) }}</td>
          </tr>
        </tbody>
      </table>
      <div v-else-if="!isLoading && !lifecycleLoading" class="empty-table">{{ emptyText }}</div>
    </div>
  </div>
</template>

<script>
import LogistsCompareChart from './LogistsCompareChart.vue';
import { fmtDate, fmtHoursNum, fmtMoney, fmtNum, fmtPct } from '../format';

const MODES = [
  { id: 'all', label: 'Все' },
  { id: 'new', label: 'Новые' },
  { id: 'stale', label: 'Давно не работали' },
];

const LIMITS = [
  { id: 'all', label: 'Все' },
  { id: 5, label: '5' },
  { id: 10, label: '10' },
  { id: 20, label: '20' },
  { id: 50, label: '50' },
];

const METRICS = [
  { field: 'trips', label: 'Рейсов', formatter: fmtNum, color: '#3b82f6' },
  { field: 'total_commission', label: 'Комиссия', formatter: fmtMoney, color: '#3b82f6' },
  { field: 'avg_commission', label: 'Ср. комиссия', formatter: fmtMoney, color: '#667085' },
  { field: 'avg_margin_pct', label: 'Ср. маржа', formatter: fmtPct, color: '#12b76a' },
  { field: 'avg_profitability_pct', label: 'Ср. рентаб.', formatter: fmtPct, color: '#f79009' },
  { field: 'avg_search_sec', label: 'Ср. поиск, ч.', formatter: fmtHoursNum, color: '#7a5af8' },
  { field: 'avg_hours_to_load', label: 'До погрузки, ч', formatter: (v) => fmtNum(v, 1), color: '#667085' },
  { field: 'avg_created_lead_hours', label: 'Создан заранее, ч', formatter: (v) => fmtNum(v, 1), color: '#98a2b3' },
  { field: 'closed_early', label: 'Закрыто ≥24 ч', formatter: fmtNum, color: '#12b76a' },
  { field: 'closed_lastday', label: 'Закрыто 12–24 ч', formatter: fmtNum, color: '#f79009' },
  { field: 'closed_late', label: 'Закрыто <12 ч', formatter: fmtNum, color: '#f04438' },
];

const METRIC_GROUPS = [
  { label: 'Объёмы', fields: ['trips'] },
  { label: 'Деньги', fields: ['total_commission', 'avg_commission'] },
  { label: 'Маржинальность', fields: ['avg_margin_pct', 'avg_profitability_pct'] },
  { label: 'Сроки', fields: ['avg_search_sec', 'avg_hours_to_load', 'avg_created_lead_hours'] },
  { label: 'Закрытие', fields: ['closed_early', 'closed_lastday', 'closed_late'] },
];

// Сохранённый вид вкладки: режим + метрики графика + сортировка + лимит строк
const VIEW_STORAGE_KEY = 'statisticsDirectionsView';

function loadView() {
  const fallback = { activeMode: 'all', fields: ['total_commission'], sortKey: 'trips', sortDir: 'desc', rowLimit: 'all' };
  try {
    const raw = window.localStorage.getItem(VIEW_STORAGE_KEY);
    if (!raw) return fallback;
    const parsed = JSON.parse(raw);
    const known = new Set(METRICS.map((m) => m.field));
    const fields = Array.isArray(parsed?.fields) ? parsed.fields.filter((f) => typeof f === 'string' && known.has(f)) : [];
    if (!fields.length) fields.push('total_commission');
    const activeMode = MODES.some((m) => m.id === parsed?.activeMode) ? parsed.activeMode : 'all';
    const rowLimit = LIMITS.some((l) => l.id === parsed?.rowLimit) ? parsed.rowLimit : 'all';
    return {
      activeMode,
      fields,
      sortKey: typeof parsed?.sortKey === 'string' ? parsed.sortKey : fallback.sortKey,
      sortDir: parsed?.sortDir === 'asc' ? 'asc' : 'desc',
      rowLimit,
    };
  } catch (e) {
    return fallback;
  }
}

export default {
  name: 'DirectionsTab',
  components: { LogistsCompareChart },
  props: {
    rows: { type: Array, default: () => [] },
    allHistoryRows: { type: Array, default: () => [] },
    lifecycleRows: { type: Array, default: () => [] },
    minTrips: { type: Number, default: 5 },
    isLoading: { type: Boolean, default: false },
    allHistoryLoading: { type: Boolean, default: false },
    lifecycleLoading: { type: Boolean, default: false },
    dateFrom: { type: String, default: '' },
  },
  data() {
    const view = loadView();
    return {
      activeMode: view.activeMode,
      sortKey: view.sortKey,
      sortDir: view.sortDir,
      selectedFields: view.fields,
      rowLimit: view.rowLimit,
    };
  },
  watch: {
    selectedFields: {
      handler() {
        this.saveView();
      },
      deep: true,
    },
    activeMode() {
      this.saveView();
    },
    sortKey() {
      this.saveView();
    },
    sortDir() {
      this.saveView();
    },
    rowLimit() {
      this.saveView();
    },
  },
  computed: {
    MODES() {
      return MODES;
    },
    LIMITS() {
      return LIMITS;
    },
    newSet() {
      if (!this.dateFrom) return new Set();
      return new Set(
        this.allHistoryRows
          .filter((row) => row.first_trip_date && this.isoDate(row.first_trip_date) >= this.dateFrom)
          .map((row) => row.direction),
      );
    },
    staleSet() {
      if (!this.dateFrom) return new Set();
      return new Set(
        this.lifecycleRows
          .filter((row) => row.last_trip_date && this.isoDate(row.last_trip_date) < this.dateFrom)
          .map((row) => row.direction),
      );
    },
    displayRows() {
      if (this.activeMode === 'new') {
        // Новое направление: первый рейс внутри периода. Вся его история
        // лежит в периоде, поэтому берём агрегаты за всю историю (min_trips=1),
        // а не периодную выборку с порогом.
        return this.allHistoryRows
          .filter((row) => this.newSet.has(row.direction))
          .slice()
          .sort((a, b) => this.isoDate(b.first_trip_date).localeCompare(this.isoDate(a.first_trip_date)));
      }
      if (this.activeMode === 'stale') {
        return this.allHistoryRows
          .filter((row) => this.staleSet.has(row.direction))
          .slice()
          .sort((a, b) => this.isoDate(a.last_trip_date).localeCompare(this.isoDate(b.last_trip_date)));
      }
      return this.rows;
    },
    tableRows() {
      const field = this.sortKey;
      const dir = this.sortDir === 'desc' ? -1 : 1;
      return this.displayRows.slice().sort((a, b) => {
        if (field === 'direction') {
          return String(a.direction || '').localeCompare(String(b.direction || ''), 'ru') * dir;
        }
        let av;
        let bv;
        if (field === 'first_trip_date' || field === 'last_trip_date') {
          av = this.isoDate(a[field]);
          bv = this.isoDate(b[field]);
          if (!av && !bv) return 0;
          if (!av) return 1;
          if (!bv) return -1;
          return av.localeCompare(bv) * dir;
        }
        const an = Number(a[field]);
        const bn = Number(b[field]);
        av = Number.isFinite(an) ? an : null;
        bv = Number.isFinite(bn) ? bn : null;
        if (av === null && bv === null) return 0;
        if (av === null) return 1;
        if (bv === null) return -1;
        return (av - bv) * dir;
      });
    },
    modeHint() {
      if (this.activeMode === 'new') {
        return 'Направления, первый рейс по которым состоялся в выбранном периоде — фильтр применяется к графикам и таблице';
      }
      if (this.activeMode === 'stale') {
        return 'Направления, по которым не было рейсов в выбранном периоде (последний рейс до его начала) — показатели за всю историю';
      }
      return '';
    },
    emptyText() {
      if (this.activeMode === 'new') {
        return this.dateFrom
          ? 'Новых направлений в периоде нет'
          : 'Выберите период (не «Всё время»), чтобы увидеть новые направления';
      }
      if (this.activeMode === 'stale') {
        return this.dateFrom
          ? 'Все направления активны в периоде'
          : 'Выберите период (не «Всё время»), чтобы увидеть неактивные направления';
      }
      return `Нет направлений с минимум ${this.minTrips} рейсами по выбранным фильтрам`;
    },
    metricGroups() {
      return METRIC_GROUPS.map((g) => ({
        label: g.label,
        metrics: g.fields.map((f) => this.metricByField(f)).filter(Boolean),
      }));
    },
    selectedMetrics() {
      return this.selectedFields.map((f) => this.metricByField(f)).filter(Boolean);
    },
    chartRows() {
      const field = this.selectedMetrics.length ? this.selectedMetrics[0].field : 'trips';
      const rows = this.displayRows
        .filter((row) => row[field] != null)
        .slice()
        .sort((a, b) => (Number(b[field]) || 0) - (Number(a[field]) || 0));
      return (typeof this.rowLimit === 'number' ? rows.slice(0, this.rowLimit) : rows)
        .map((row) => ({ ...row, logist: row.direction }));
    },
    maxMargin() {
      return Math.max(...this.displayRows.map((r) => Math.abs(Number(r.avg_margin_pct) || 0)), 1);
    },
    maxProfitability() {
      return Math.max(...this.displayRows.map((r) => Math.abs(Number(r.avg_profitability_pct) || 0)), 1);
    },
  },
  methods: {
    saveView() {
      try {
        window.localStorage.setItem(
          VIEW_STORAGE_KEY,
          JSON.stringify({
            activeMode: this.activeMode,
            fields: this.selectedFields,
            sortKey: this.sortKey,
            sortDir: this.sortDir,
            rowLimit: this.rowLimit,
          })
        );
      } catch (e) {
        /* localStorage может быть недоступен — молча игнорируем */
      }
    },
    metricByField(field) {
      return METRICS.find((m) => m.field === field) || METRICS[0];
    },
    isSelected(metric) {
      return this.selectedFields.includes(metric.field);
    },
    toggleMetric(metric, event) {
      if (event && event.shiftKey) {
        this.selectedFields = [metric.field];
        return;
      }
      if (this.selectedFields.includes(metric.field)) {
        if (this.selectedFields.length === 1) return;
        this.selectedFields = this.selectedFields.filter((f) => f !== metric.field);
        return;
      }
      this.selectedFields = [...this.selectedFields, metric.field];
    },
    fmtDate,
    fmtHoursNum,
    fmtMoney,
    fmtNum,
    fmtPct,
    setSort(key) {
      if (this.sortKey === key) {
        this.sortDir = this.sortDir === 'desc' ? 'asc' : 'desc';
        return;
      }
      this.sortKey = key;
      this.sortDir = key === 'direction' || key === 'first_trip_date' || key === 'last_trip_date' ? 'asc' : 'desc';
    },
    // Даты из API приходят epoch-ms (pandas to_json) либо ISO-строкой.
    isoDate(value) {
      if (value === null || value === undefined || value === '') return '';
      if (typeof value === 'number') {
        const ms = value > 1e12 ? value : value * 1000;
        return new Date(ms).toISOString().slice(0, 10);
      }
      return String(value).slice(0, 10);
    },
    negClass(value) {
      return Number(value) < 0 ? 'neg-cell' : null;
    },
    barClass(value) {
      return Number(value) < 0 ? 'bar-neg' : 'bar-pos';
    },
    // Двунаправленный бар: от центральной оси вправо (плюс) / влево (минус)
    barStyle(value, max) {
      const abs = Math.abs(Number(value) || 0);
      const width = Math.min(50, Math.round((abs / max) * 50));
      const side = Number(value) < 0 ? 'right' : 'left';
      return { width: `${width}%`, [side]: '50%' };
    },
    closeTotal(row) {
      return (Number(row.closed_early) || 0) + (Number(row.closed_lastday) || 0) + (Number(row.closed_late) || 0);
    },
    closePct(row, field) {
      const total = this.closeTotal(row);
      if (!total) return 0;
      return Math.round(((Number(row[field]) || 0) / total) * 100);
    },
    closeTitle(row) {
      const total = this.closeTotal(row);
      if (!total) return '';
      const part = (field) => `${this.fmtNum(row[field])} (${Math.round(((Number(row[field]) || 0) / total) * 100)}%)`;
      return `Закрытия (всего ${this.fmtNum(total)}):\n≥24 ч: ${part('closed_early')}\n12–24 ч: ${part('closed_lastday')}\n<12 ч: ${part('closed_late')}`;
    },
  },
};
</script>

<style scoped>
.directions-tab {
  display: flex;
  flex-direction: column;
  gap: 16px;
  min-width: 0;
}

.mode-row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 12px;
  flex-wrap: wrap;
}

.mode-chips {
  display: flex;
  gap: 6px;
}

.limit-chips {
  display: flex;
  align-items: center;
  gap: 4px;
}

.limit-btn {
  padding: 5px 10px;
  font-size: 12px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #475467;
  cursor: pointer;
}

.limit-btn:hover {
  background: #f8fafc;
}

.limit-btn.active {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #007bff;
  font-weight: 600;
}

.mode-btn {
  padding: 7px 14px;
  font-size: 13px;
  border: 1px solid #d0d5dd;
  border-radius: 6px;
  background: #fff;
  color: #475467;
  cursor: pointer;
}

.mode-btn:hover {
  background: #f8fafc;
}

.mode-btn.active {
  background: #eff6ff;
  border-color: #3b82f6;
  color: #007bff;
  font-weight: 600;
}

.mode-hint {
  font-size: 13px;
  color: #667085;
  background: #f8fafc;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  padding: 10px 14px;
}

.chart-layout {
  display: grid;
  grid-template-columns: 300px 1fr;
  gap: 16px;
  align-items: stretch;
}

@media (max-width: 1100px) {
  .chart-layout {
    grid-template-columns: 1fr;
  }
}

.chart-settings {
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
  display: flex;
  flex-direction: column;
  gap: 14px;
}

.settings-title {
  font-size: 14px;
  font-weight: 600;
  color: #344054;
}

.settings-section {
  display: flex;
  flex-direction: column;
  gap: 8px;
}

.settings-label {
  font-size: 12px;
  font-weight: 500;
  color: #667085;
}

.metrics-list {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
}

.metric-chip {
  display: inline-flex;
  align-items: center;
  gap: 6px;
  padding: 4px 10px;
  border-radius: 14px;
  border: 1px solid #d0d5dd;
  background: #fff;
  font-size: 12px;
  color: #344054;
  cursor: pointer;
}

.metric-chip:hover {
  border-color: #98a2b3;
}

.metric-chip.selected {
  border-color: #007bff;
  background: #eff6ff;
}

.metric-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  flex: none;
}

.settings-hint {
  font-size: 11px;
  color: #98a2b3;
}

.chart-block {
  min-width: 0;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
  padding: 14px;
}

.table-scroll {
  overflow-x: auto;
  border: 1px solid #e5e7eb;
  border-radius: 8px;
  background: #fff;
}

.stat-table {
  width: 100%;
  border-collapse: collapse;
  font-size: 13px;
}

.stat-table th {
  position: sticky;
  top: 0;
  background: #f8fafc;
  color: #667085;
  font-weight: 600;
  text-align: left;
  padding: 10px 12px;
  border-bottom: 1px solid #e5e7eb;
  white-space: nowrap;
}

.stat-table th.sortable {
  cursor: pointer;
  user-select: none;
}

.stat-table th.sortable:hover {
  color: #344054;
}

.stat-table th.active {
  color: #007bff;
}

.sort-arrow {
  font-size: 9px;
}

.stat-table td {
  padding: 8px 12px;
  border-bottom: 1px solid #f2f4f7;
  color: #1d2939;
  white-space: nowrap;
}

.stat-table tbody tr:hover {
  background: #f8fafc;
}

.num {
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.strong {
  font-weight: 600;
}

.dir-cell {
  font-weight: 500;
}

.neg-cell {
  color: #d92d20;
}

.dir-early {
  color: #067647;
}

.dir-late {
  color: #d92d20;
}

.cell-wide {
  min-width: 210px;
}

.bar-cell {
  display: flex;
  align-items: center;
  gap: 8px;
  margin-left: auto;
}

.bar-track {
  width: 110px;
  height: 6px;
  border-radius: 3px;
  background: #f2f4f7;
  flex-shrink: 0;
  position: relative;
}

/* центральная ось нуля двунаправленного бара */
.bar-track::after {
  content: '';
  position: absolute;
  left: 50%;
  top: -2px;
  bottom: -2px;
  width: 1px;
  background: #98a2b3;
}

.bar-value {
  min-width: 58px;
  text-align: right;
  font-variant-numeric: tabular-nums;
}

.bar-fill {
  height: 100%;
  position: absolute;
  top: 0;
}

.bar-pos {
  background: #12b76a;
  border-radius: 0 3px 3px 0;
}

.bar-neg {
  background: #f04438;
  border-radius: 3px 0 0 3px;
}

.close-widget {
  display: inline-block;
}

.close-track {
  width: 90px;
  height: 8px;
  border-radius: 4px;
  background: #f2f4f7;
  overflow: hidden;
  display: flex;
}

.close-seg {
  height: 100%;
  min-width: 0;
}

.seg-early {
  background: #12b76a;
}

.seg-lastday {
  background: #f79009;
}

.seg-late {
  background: #f04438;
}

.first-badge {
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #ecfdf3;
  color: #027a48;
  font-size: 11px;
  font-weight: 500;
}

.stale-badge {
  margin-left: 6px;
  padding: 2px 8px;
  border-radius: 10px;
  background: #fffcf5;
  color: #93370d;
  font-size: 11px;
  font-weight: 500;
}

.empty-table {
  padding: 40px;
  text-align: center;
  color: #98a2b3;
  font-size: 14px;
}
</style>
